import { ConfigurationRepositoryWorkspacesService } from '@festina/configuration-general-types/configuration/services/ConfigurationRepositoryWorkspacesService'
import {
  assertNotEmpty,
  type ConfigurableServer,
  type Except,
  notEmpty,
  type Optional,
} from '@festina-life/core'
import { useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'

import { fetchWorkspaceErrorsOnLayersKey } from '../keys/configuration'
import { fetchAndDeduplicateErrors } from './errors/helpers'
import type { LayerKey } from './layers/LayerKey'
import type { Tree } from './types'

type FetchWorkspaceErrorProps = {
  server: Optional<ConfigurableServer>
  workspaceName: Optional<string>
  layers: LayerKey[]
  blocks: string[]
  layerTopology: Optional<Tree>
  blockTopology: Optional<Tree>
}

type Options = Except<Parameters<typeof useQuery>[0], 'queryKey' | 'queryFn'>

const isEnabled = (props: FetchWorkspaceErrorProps, options: Options) =>
  options.enabled !== false && !!props.server && !!props.workspaceName

export const useFetchWorkspaceErrors = (
  props: FetchWorkspaceErrorProps,
  options: Options,
) => {
  const fetcher = useCallback(
    async ({ layer, block }: { layer: LayerKey; block: string }) => {
      if (!isEnabled(props, options)) {
        return []
      }

      const { server, workspaceName } = props

      assertNotEmpty(server)
      assertNotEmpty(workspaceName)

      const result = await (layer.layerId
        ? ConfigurationRepositoryWorkspacesService.workspaceServiceGetGetWorkspaceLayerErrors(
            {
              componentName: server.toLocaleLowerCase(),
              workspaceName,
              layer: layer.layerId,
              tenantId: layer.tenantId ?? undefined,
              block,
            },
          )
        : ConfigurationRepositoryWorkspacesService.workspaceServiceGetGetWorkspaceErrors(
            {
              componentName: server.toLocaleLowerCase(),
              workspaceName,
              block,
              tenantId: layer.tenantId ?? undefined,
            },
          ))

      return (result?.errors ?? []).filter(notEmpty)
    },
    [options, props],
  )

  return useQuery({
    enabled: isEnabled(props, options),
    queryKey: fetchWorkspaceErrorsOnLayersKey({
      server: props.server ?? ('' as ConfigurableServer),
      workspaceName: props.workspaceName ?? '',
      layers: props.layers,
    }),
    queryFn: () => {
      if (!isEnabled(props, options)) {
        return Promise.resolve(null)
      }

      return fetchAndDeduplicateErrors({
        blocks: props.blocks,
        layers: props.layers,
        fetcher,
        layerTopology: props.layerTopology,
        blockTopology: props.blockTopology,
      })
    },
    retry: false,
    staleTime: Infinity,
  })
}
